import React, { useEffect, useState, useMemo, useCallback } from "react";
import Context from "../../Context";
import { useToast, Spinner } from "../../components/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { Header } from "../../components/modules";
import { MdSearch, MdClear } from "react-icons/md";
import {
  FiCheckCircle,
  FiAlertCircle,
  FiExternalLink,
  FiPackage,
  FiTrendingUp,
} from "react-icons/fi";
import { HiRefresh } from "react-icons/hi";
import { FaCheckCircle, FaTimes } from "react-icons/fa";

// ── Helpers ───────────────────────────────────────────────────────────────────

const showToast = (toast, success, successMsg, failMsg) =>
  toast({
    className: success ? "bg-green text-white" : "bg-failed text-white",
    title: (
      <div className="flex gap-2 items-center text-sm font-semibold">
        {success ? <FaCheckCircle /> : <FaTimes />}
        {success ? successMsg : failMsg || "ผิดพลาด กรุณาลองใหม่อีกครั้ง"}
      </div>
    ),
  });



export const LotteriesOnBlock = () => {
  const { callApi, configs } = Context();
  const { toast } = useToast();

  const [booksOnBlock, setBooksOnBlock] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'selling' | 'soldout'
  const [loading, setLoading] = useState(false);
  const [pullingKey, setPullingKey] = useState(null);

  // Fetch books on block
  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await callApi(
        "lotteries-countImports",
        {
          isOnBlock: true,
          roundDate: configs?.roundDate,
        },
        { notShowLoading: true }
      );
      const data = result?.data || {};
      setBooksOnBlock(data);
    } catch (error) {
      console.error("Fetch lotteries on block failed:", error);
      showToast(toast, false, null, "ดึงข้อมูลสลากบนแผงไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  }, [callApi, configs?.roundDate, toast]);

  useEffect(() => {
    getData();
  }, [getData]);

  // Pull a book from block (Unpublish / return to backend)
  const handlePullFromBlock = async (fileNameId) => {
    if (pullingKey) return;
    const confirmMsg = `ยืนยันนำสลากเล่ม "${fileNameId}" ออกจากแผงขายกลับสู่ระบบหลังบ้านหรือไม่?`;
    if (!window.confirm(confirmMsg)) return;

    setPullingKey(fileNameId);
    try {
      const res = await callApi(
        "blocks-pullFromBlock",
        { fileNameId },
        { notShowLoading: true }
      );
      if (res?.status) {
        showToast(toast, true, `นำเล่ม ${fileNameId} ออกจากแผงขายเรียบร้อยแล้ว`);
        // Remove locally from state
        setBooksOnBlock((prev) => {
          const next = { ...prev };
          delete next[fileNameId];
          return next;
        });
      } else {
        showToast(
          toast,
          false,
          null,
          res?.toastMessage || "นำเล่มออกจากระบบไม่สำเร็จ"
        );
      }
    } catch (err) {
      console.error("pullFromBlock error:", err);
      showToast(toast, false, null, "เกิดข้อผิดพลาดในการนำเล่มออกจากระบบ");
    } finally {
      setPullingKey(null);
    }
  };

  // Entries array
  const allEntries = useMemo(() => {
    return Object.entries(booksOnBlock);
  }, [booksOnBlock]);

  // Overall Statistics for current round
  const stats = useMemo(() => {
    let totalTickets = 0;
    let totalAvailableTickets = 0;
    let totalSoldTickets = 0;
    let soldOutBooksCount = 0;

    allEntries.forEach(([, val]) => {
      const remaining = Number(val?.size) || 0;
      const sold = Number(val?.isSold) || 0;
      const bookTotal = remaining + sold;

      totalTickets += bookTotal;
      totalAvailableTickets += remaining;
      totalSoldTickets += sold;

      if (bookTotal > 0 && remaining === 0) {
        soldOutBooksCount += 1;
      }
    });

    const overallPercent =
      totalTickets > 0 ? (totalSoldTickets / totalTickets) * 100 : 0;

    return {
      totalBooks: allEntries.length,
      totalTickets,
      totalAvailableTickets,
      totalSoldTickets,
      soldOutBooksCount,
      overallPercent: overallPercent.toFixed(1),
    };
  }, [allEntries]);

  // Filtered entries based on search and status
  const filteredEntries = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();

    return allEntries
      .filter(([key, val]) => {
        // Keyword filter
        if (keyword) {
          const matchKey = key.toLowerCase().includes(keyword);
          const matchUploader = (val?.uploader || "")
            .toLowerCase()
            .includes(keyword);
          if (!matchKey && !matchUploader) return false;
        }

        // Status filter
        const remaining = Number(val?.size) || 0;
        const sold = Number(val?.isSold) || 0;
        const bookTotal = remaining + sold;
        const isSoldOut = bookTotal > 0 && remaining === 0;

        if (statusFilter === "selling") {
          return !isSoldOut;
        }
        if (statusFilter === "soldout") {
          return isSoldOut;
        }
        return true;
      })
      .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
  }, [allEntries, searchTerm, statusFilter]);

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto pb-12">
      <Header
        header="สลากในระบบพร้อมขาย"
        subHeader="รายการเล่มสลากที่ปล่อยวางขายบนแผงหน้าบ้าน และสถานะยอดขายเรียลไทม์"
        icon={<FiPackage className="text-navy text-2xl" />}
      />

      {/* Summary KPI Cards */}
      <div className="mt-8 mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Card 1: Books */}
        <div className="bg-white border border-gray-200 rounded-md p-4 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500">เล่มบนแผงทั้งหมด</p>
            <p className="text-xl font-bold text-gray-900 mt-1">
              {stats.totalBooks.toLocaleString()}{" "}
              <span className="text-xs font-normal text-gray-500">เล่ม</span>
            </p>
          </div>
          <div className="w-10 h-10 rounded-md bg-blue/10 flex items-center justify-center text-blue">
            <FiPackage className="w-5 h-5" />
          </div>
        </div>

        {/* Card 2: Total Tickets */}
        <div className="bg-white border border-gray-200 rounded-md p-4 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500">สลากบนแผงทั้งหมด</p>
            <p className="text-xl font-bold text-gray-900 mt-1">
              {stats.totalTickets.toLocaleString()}{" "}
              <span className="text-xs font-normal text-gray-500">ใบ</span>
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              คงเหลือพร้อมขาย {stats.totalAvailableTickets.toLocaleString()} ใบ
            </p>
          </div>
          <div className="w-10 h-10 rounded-md bg-indigo-50 flex items-center justify-center text-indigo-600">
            <FiCheckCircle className="w-5 h-5" />
          </div>
        </div>

        {/* Card 3: Sold Tickets */}
        <div className="bg-white border border-gray-200 rounded-md p-4 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500">ขายไปแล้วทั้งหมด</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">
              {stats.totalSoldTickets.toLocaleString()}{" "}
              <span className="text-xs font-normal text-gray-500">ใบ</span>
            </p>
          </div>
          <div className="w-10 h-10 rounded-md bg-emerald-50 flex items-center justify-center text-emerald-600">
            <FiTrendingUp className="w-5 h-5" />
          </div>
        </div>

        {/* Card 4: Progress Percentage */}
        <div className="bg-white border border-gray-200 rounded-md p-4 shadow-sm flex items-center justify-between">
          <div className="w-full mr-2">
            <div className="flex justify-between items-center mb-1">
              <p className="text-xs font-semibold text-gray-500">อัตราการขายรวม</p>
              <span className="text-xs font-bold text-navy">
                {stats.overallPercent}%
              </span>
            </div>
            <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
              <div
                className="bg-navy h-full transition-all duration-500"
                style={{ width: `${Math.min(Number(stats.overallPercent), 100)}%` }}
              />
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              ขายหมดแล้ว {stats.soldOutBooksCount.toLocaleString()} เล่ม
            </p>
          </div>
        </div>
      </div>

      <div className="relative bg-white w-full rounded-md shadow-sm border border-[#E5E7E9] overflow-hidden">
        {/* Toolbar & Filters */}
        <div className="flex flex-wrap justify-between items-center border-b border-[#E5E7E9] px-4 py-3 bg-gray-50/60 gap-4">
          {/* Status Tabs */}
          <div className="flex items-center gap-1.5 bg-gray-200/60 p-1 rounded-md">
            <button
              onClick={() => setStatusFilter("all")}
              className={`text-xs font-bold py-1 px-3 rounded-md transition ${statusFilter === "all"
                  ? "bg-white text-navy shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              ทั้งหมด ({stats.totalBooks})
            </button>
            <button
              onClick={() => setStatusFilter("selling")}
              className={`text-xs font-bold py-1 px-3 rounded-md transition ${statusFilter === "selling"
                  ? "bg-white text-navy shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              กำลังขาย ({stats.totalBooks - stats.soldOutBooksCount})
            </button>
            <button
              onClick={() => setStatusFilter("soldout")}
              className={`text-xs font-bold py-1 px-3 rounded-md transition ${statusFilter === "soldout"
                  ? "bg-white text-emerald-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              ขายหมดแล้ว ({stats.soldOutBooksCount})
            </button>
          </div>

          {/* Search Box & Refresh */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                className="border border-gray-300 focus:border-blue py-1.5 pl-3 pr-8 w-64 rounded-md text-sm font-semibold placeholder:text-gray-400 focus:outline-none"
                type="text"
                placeholder="ค้นหาชื่อเล่ม (เช่น 0000-100)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm ? (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <MdClear className="w-4 h-4" />
                </button>
              ) : (
                <MdSearch className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              )}
            </div>

            <button
              className="p-1.5 bg-white border border-gray-300 text-gray-700 text-base rounded-md hover:bg-gray-100 transition shadow-sm"
              onClick={getData}
              disabled={loading}
              title="รีเฟรชข้อมูล"
            >
              <HiRefresh className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div className="relative min-h-[450px]">
          {loading && (
            <div className="absolute inset-0 flex flex-col justify-center items-center bg-white/70 backdrop-blur-[1px] z-20">
              <Spinner className="w-8 h-8" />
              <span className="mt-2 text-xs font-semibold text-navy">
                กำลังโหลดข้อมูลสลากบนแผง...
              </span>
            </div>
          )}

          <Table>
            <TableHeader className="bg-[#f8f9fa] border-b border-gray-200">
              <TableRow>
                <TableHead className="text-center py-3 w-12 text-xs font-bold">
                  ลำดับ
                </TableHead>
                <TableHead className="text-center w-36 text-xs font-bold">
                  จัดการ
                </TableHead>
                <TableHead className="text-center w-24 text-xs font-bold">
                  ตรวจสอบ
                </TableHead>
                <TableHead className="text-xs font-bold">
                  ชื่อเล่ม (Zip file)
                </TableHead>
                <TableHead className="text-center text-xs font-bold w-32">
                  สลากทั้งหมด
                </TableHead>
                <TableHead className="text-center text-xs font-bold w-48">
                  สถานะการขาย (ใบ)
                </TableHead>
                <TableHead className="text-center text-xs font-bold w-36">
                  ความคืบหน้า (%)
                </TableHead>
                <TableHead className="text-center text-xs font-bold">
                  ผู้นำเข้า
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEntries.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <div className="min-h-60 flex flex-col justify-center items-center text-gray-400 font-medium gap-2">
                      <FiPackage className="w-8 h-8 text-gray-300" />
                      <span>
                        {searchTerm
                          ? `ไม่พบเล่มสลากที่ตรงกับ "${searchTerm}"`
                          : "ไม่พบเล่มสลากในสถานะนี้"}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEntries.map(([key, value], index) => {
                  const remaining = Number(value?.size) || 0;
                  const soldNum = Number(value?.isSold) || 0;
                  const bookTotal = remaining + soldNum;
                  const soldPercent =
                    bookTotal > 0 ? (soldNum / bookTotal) * 100 : 0;
                  const isSoldOut = bookTotal > 0 && remaining === 0;
                  const isPulling = pullingKey === key;

                  return (
                    <TableRow
                      key={key}
                      className="hover:bg-blue/5 transition-colors"
                    >
                      {/* Index */}
                      <TableCell className="text-center font-mono text-xs text-gray-500">
                        {index + 1}
                      </TableCell>

                      {/* Action: Pull from block */}
                      <TableCell className="text-center">
                        <button
                          disabled={isPulling}
                          onClick={() => handlePullFromBlock(key)}
                          className="inline-flex items-center gap-1 bg-failed/10 text-failed hover:bg-failed hover:text-white text-xs font-semibold py-1.5 px-3 rounded-md transition duration-150 shadow-sm disabled:opacity-40"
                          title="นำเล่มนี้ออกจากแผงขายหน้าบ้าน"
                        >
                          {isPulling ? (
                            <Spinner className="w-3.5 h-3.5" color="border-failed" />
                          ) : null}
                          <span>นำออกจากระบบ</span>
                        </button>
                      </TableCell>

                      {/* Detail Link */}
                      <TableCell className="text-center">
                        <button
                          className="inline-flex items-center gap-1 bg-white border border-gray-300 text-gray-700 hover:bg-blue hover:text-white hover:border-blue text-xs font-semibold py-1.5 px-2.5 rounded-md transition duration-150 shadow-sm"
                          onClick={() =>
                            window.open(
                              `/lotteries/detail?fileNameId=${encodeURIComponent(
                                key
                              )}`,
                              "_blank"
                            )
                          }
                          title="ดูสลากทุกใบในเล่มนี้"
                        >
                          <span>ดูข้อมูล</span>
                          <FiExternalLink className="w-3 h-3" />
                        </button>
                      </TableCell>

                      {/* Book Name */}
                      <TableCell className="font-semibold text-sm text-gray-800">
                        {key}
                      </TableCell>

                      {/* Total Tickets Badge */}
                      <TableCell className="text-center">
                        {bookTotal === 100 ? (
                          <span className="font-bold text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-md">
                            100 ใบ
                          </span>
                        ) : bookTotal > 100 ? (
                          <span
                            className="font-bold text-xs text-blue bg-blue/10 border border-blue/20 px-2.5 py-0.5 rounded-md"
                            title={`เล่มพิเศษมี ${bookTotal} ใบ`}
                          >
                            {bookTotal} ใบ
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 font-bold text-xs text-amber-800 bg-amber-50 border border-amber-300 px-2 py-0.5 rounded-md"
                            title="สลากเล่มนี้มีน้อยกว่า 100 ใบ"
                          >
                            <FiAlertCircle className="text-amber-600 w-3.5 h-3.5" />
                            {bookTotal} ใบ
                          </span>
                        )}
                      </TableCell>

                      {/* Sold Tickets & Progress Bar */}
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="font-semibold text-xs text-gray-700">
                            {soldNum.toLocaleString()} / {bookTotal.toLocaleString()} ใบ
                          </span>
                          <div className="w-32 bg-gray-200 h-1.5 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all duration-300 ${
                                isSoldOut
                                  ? "bg-emerald-500"
                                  : soldPercent > 0
                                  ? "bg-blue"
                                  : "bg-transparent"
                              }`}
                              style={{ width: `${Math.min(soldPercent, 100)}%` }}
                            />
                          </div>
                          <span className="text-[11px] text-gray-400">
                            (เหลือ {remaining.toLocaleString()} ใบ)
                          </span>
                        </div>
                      </TableCell>

                      {/* Sold Percentage Badge */}
                      <TableCell className="text-center">
                        {isSoldOut ? (
                          <span className="font-bold text-xs text-emerald-800 bg-emerald-100 border border-emerald-300 px-2.5 py-0.5 rounded-md">
                            100% (ขายหมด)
                          </span>
                        ) : soldPercent > 0 ? (
                          <span className="font-semibold text-xs text-blue bg-blue/10 border border-blue/20 px-2.5 py-0.5 rounded-md">
                            {soldPercent.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-md">
                            0%
                          </span>
                        )}
                      </TableCell>

                      {/* Uploader */}
                      <TableCell className="text-center text-sm text-gray-600">
                        {Array.isArray(value?.uploader)
                          ? value.uploader.join(", ")
                          : value?.uploader || "-"}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default LotteriesOnBlock;
